import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { PageHeader, Empty, istDT } from "../../components/common";
import { Pill, fmtD, useLoad, useOpsMeta, btn, btn2 } from "../../lib/ops";

const OPS = { add_step: "Add a step", edit_step: "Edit a step", cancel_step: "Cancel a step", add_line: "Add a delivery line", edit_line: "Edit a delivery line",
  lock: "Fix the deadlines", replan: "Re-plan dates", apply_template: "Add steps from a template" };
const change = (r, meta) => {
  const p = r.payload || {};
  if (r.kind === "plan_change") return OPS[p.op] || "Plan change";
  if (r.kind === "baseline_change") return `Deadline ${fmtD(p.from?.[1])} → ${fmtD(p.to?.[1])} (start ${fmtD(p.from?.[0])} → ${fmtD(p.to?.[0])})`;
  if (r.kind === "postpone") return `Customer date ${fmtD(p.from)} → ${fmtD(p.to)}`;
  if (r.kind === "exclusion") return meta.exclusion_codes[p.code] || p.code;
  return "Start production before the PO arrives";
};
const TONES = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-700" };

const ReqCard = ({ r, meta, canDecide, onDecide }) => (
  <div className="ax-panel p-4" data-testid={`ops-req-${r.request_id}`}>
    <div className="flex justify-between gap-2 flex-wrap">
      <div className="min-w-0"><div className="font-semibold">{r.kind_label}</div>
        <div className="text-xs text-muted-foreground"><Link to={r.kind === "prepo_start" ? "/ops" : `/ops/uid/${r.uid}`} className="font-mono text-primary">{r.uid}</Link> · {r.client_name} · {r.label}</div></div>
      <Pill tone={TONES[r.status]} tid={`ops-req-status-${r.request_id}`}>{r.status}</Pill>
    </div>
    <div className="text-sm mt-2">{change(r, meta)}</div>
    <div className="text-sm mt-1 text-muted-foreground">{r.reason ? `“${r.reason}” — ` : ""}{r.requested_by_name}, {istDT(r.at)}</div>
    {r.decided_by_name && <div className="text-xs mt-1">{r.status === "approved" ? "Approved" : "Rejected"} by {r.decided_by_name}, {istDT(r.decided_at)}{r.note ? ` · ${r.note}` : ""}</div>}
    {canDecide && <div className="flex gap-2 mt-3">
      <button data-testid={`ops-req-approve-${r.request_id}`} onClick={() => onDecide(r, true)} className={btn}>Approve</button>
      <button data-testid={`ops-req-reject-${r.request_id}`} onClick={() => onDecide(r, false)} className={btn2}>Reject</button></div>}
  </div>
);

export default function OpsApprovals() {
  const meta = useOpsMeta();
  const [status, setStatus] = useState("pending");
  const [rows, load] = useLoad(`/ops/requests?status=${status}`);
  const decide = (r, approve) => {
    const note = window.prompt(approve ? "Note (optional)" : "Why reject? (optional)");
    if (note === null) return;
    api.post(`/ops/requests/${r.request_id}/decide`, { approve, note }).then(() => { toast.success(approve ? "Approved" : "Rejected"); load(); }).catch((e) => toast.error(errMsg(e)));
  };
  if (!meta) return <div className="text-sm text-muted-foreground">Loading…</div>;
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Operations" title="Ops approvals" desc={meta.can.approve ? "Deadline changes, score exclusions, customer postponements and Pre-PO starts need your decision. In Mumbai, every plan change also needs the Managing Partner's (or a founder's) approval. Original dates are always kept." : "Requests you have sent for approval."} />
      <div className="flex border w-fit mb-4">{["pending", "approved", "rejected"].map((k) => <button key={k} data-testid={`ops-req-tab-${k}`} onClick={() => setStatus(k)} className={`h-9 px-3 text-sm capitalize ${status === k ? "bg-primary text-primary-foreground" : ""}`}>{k}</button>)}</div>
      {!rows ? <div className="text-sm text-muted-foreground">Loading…</div> : !rows.length ? <Empty text="Nothing here." /> : (
        <div className="grid gap-3" data-testid="ops-req-list">{rows.map((r) => <ReqCard key={r.request_id} r={r} meta={meta} onDecide={decide}
          canDecide={!!r.can_decide} />)}</div>)}
    </div>
  );
}
